import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { setLanguage } from '@/app/i18n';
import { useAuthStore } from '@/stores/auth';
import { useIsMobile } from '@/hooks/useIsMobile';
import { tapHeavy } from '@/lib/haptics';
import { splitStaffNav, type StaffRole } from '@/lib/staffMore';
import { useShellContext } from '@/components/layout/shellContext';
import { NavBadge } from '@/components/layout/NavBadge';
import { MBlock, MIcon, MLabel, MRow, MSegmented, MTitle } from '@/components/mobile/MobileKit';

const HOME: Record<StaffRole, string> = { curator: '/curator', admin: '/admin' };

/**
 * «Ещё» сотрудника на телефоне (13.10.2026, docs/CURATOR-MOBILE-DESIGN.md §2): все разделы, которых нет в нижнем
 * меню, профиль, язык и «Выйти». Отдельный экран, а не всплывашка — «назад», свайп iPhone и кнопка Android работают
 * как везде. На широком экране его нет: там боковое меню.
 *
 * Пункты приходят из оболочки уже отфильтрованными (роль, выключатели, приложение) — те же правила, что прячут их в
 * боковом меню; здесь они только раскладываются по группам (`lib/staffMore.ts`).
 */
export function StaffMorePage({ role }: { role: StaffRole }) {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const user = useAuthStore((s) => s.user);
  const { logout, navItems, unread } = useShellContext();

  if (!isMobile) return <Navigate to={HOME[role]} replace />;

  const { groups, profile } = splitStaffNav(role, navItems);
  const lang = (['kk', 'ru', 'en'] as const).find((l) => i18n.language?.startsWith(l)) ?? 'ru';
  const subtitle = [user?.full_name, user?.email].filter(Boolean).join(' · ');

  return (
    <div>
      <MTitle title={t('nav.more')} subtitle={subtitle || undefined} />

      {groups.map((group) => (
        <div key={group.labelKey}>
          <MLabel>{t(group.labelKey)}</MLabel>
          <MBlock>
            {group.items.map((item, i) => (
              <MRow
                key={item.to}
                to={item.to}
                leading={<MIcon icon={item.icon} tone="muted" />}
                title={t(item.labelKey)}
                last={i === group.items.length - 1}
                trailing={
                  item.badge === 'notifications' && unread > 0 ? (
                    <span className="flex flex-none items-center gap-1">
                      <NavBadge count={unread} />
                      <span className="material-symbols-outlined text-[17px] text-on-surface-variant">
                        chevron_right
                      </span>
                    </span>
                  ) : undefined
                }
              />
            ))}
          </MBlock>
        </div>
      ))}

      <MLabel>{t('profile.interfaceLang')}</MLabel>
      <MSegmented
        tabs={[
          { key: 'kk', label: 'Қазақша' },
          { key: 'ru', label: 'Русский' },
          { key: 'en', label: 'English' },
        ]}
        active={lang}
        onChange={(l) => setLanguage(l)}
      />

      <MLabel>{t('staffMore.account')}</MLabel>
      <MBlock>
        {profile && (
          <MRow
            to={profile.to}
            leading={<MIcon icon={profile.icon} tone="muted" />}
            title={t(profile.labelKey)}
          />
        )}
        {/* «Выйти» — как у ученика в настройках: последняя строка, серый значок, без подтверждения. Выход — тот же
            `handleLogout` оболочки (метка пушей, отзыв сессии, «вышел сам»), своей копии здесь нет. */}
        <MRow
          title={t('common.logout')}
          last
          onClick={() => {
            tapHeavy();
            void logout();
          }}
          trailing={
            <span className="material-symbols-outlined flex-none text-[17px] text-on-surface-variant">logout</span>
          }
        />
      </MBlock>
    </div>
  );
}
